import { c as _c } from "react/compiler-runtime";
import { useContext, useMemo } from 'react';
import { SupportedLanguageKeys } from '@services/responseTypes';
import { useLFSearchParams } from '@services/useLFSearchParams';
import { useQueryOrg } from '@services/useQueryOrg';
import { RootStoreContext } from '@stores/rootStore';
import { isPresent } from '@farmai-shared/LFUtils';
type Labeled = {
  en?: string;
  native?: string;
} & Partial<Record<SupportedLanguageKeys, string>>;
type Level = 'country' | 'province' | 'district' | 'subdistrict' | 'village';
type LevelDataMap = {
  country: {
    countries: Labeled[];
  };
  province: {
    regions: Labeled[];
  };
  district: {
    districts: Labeled[];
  };
  subdistrict: {
    subdistricts: Labeled[];
  };
  village: {
    villages: Labeled[];
  };
};
const DEFAULTS: LevelDataMap = {
  country: {
    countries: []
  },
  province: {
    regions: []
  },
  district: {
    districts: []
  },
  subdistrict: {
    subdistricts: []
  },
  village: {
    villages: []
  }
};
const LEVEL_ORDER: Level[] = ['country', 'province', 'district', 'subdistrict', 'village'];
const PARAM_KEY_BY_LEVEL: Record<Level, `filter[${string}]`> = {
  country: 'filter[country]',
  province: 'filter[province]',
  district: 'filter[district]',
  subdistrict: 'filter[subdistrict]',
  village: 'filter[village]'
};
const dependentLevels = (lvl: Level): Level[] => {
  const idx = LEVEL_ORDER.indexOf(lvl);
  return idx >= 0 ? LEVEL_ORDER.slice(idx + 1) : [];
};
interface UseAdministrativeLevelsQueryProps<L extends Level> {
  level: L;
  enabled?: boolean;
  queryKey?: (string | number)[];
  fetchAdministrativeLevels: () => Promise<LevelDataMap[L]>;
}
export const useAdministrativeLevelsQuery = t0 => {
  const $ = _c(12);
  const {
    level,
    enabled: t1,
    fetchAdministrativeLevels,
    queryKey: t2
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t3;
  if ($[0] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const queryKey = t3;
  const defaultData = DEFAULTS[level];
  let t4;
  if ($[2] !== level || $[3] !== queryKey) {
    t4 = ["overview", level, ...queryKey];
    $[2] = level;
    $[3] = queryKey;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== defaultData || $[6] !== fetchAdministrativeLevels) {
    t5 = () => fetchAdministrativeLevels().catch(() => defaultData);
    $[5] = defaultData;
    $[6] = fetchAdministrativeLevels;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== enabled || $[9] !== t4 || $[10] !== t5) {
    t6 = {
      queryKey: t4,
      queryFn: t5,
      refetchOnWindowFocus: false,
      enabled
    };
    $[8] = enabled;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return useQueryOrg(t6);
};
type CollectionKeyByLevel = {
  country: 'countries';
  province: 'regions';
  district: 'districts';
  subdistrict: 'subdistricts';
  village: 'villages';
};
type CollectionKey<L extends Level> = CollectionKeyByLevel[L];
type ParentDep = {
  paramKey: `filter[${string}]`;
  argName: string;
  required?: boolean; // default true
};
type UseActiveAdministrativeOpts<L extends Level> = {
  level: L;
  paramKey: `filter[${string}]`;
  parents?: ParentDep[];
};
type DataForLevel<L extends Level> = { [K in CollectionKey<L>]: Labeled[] };
export const useActiveAdministrative = t0 => {
  const $ = _c(41);
  const {
    parents,
    paramKey,
    level
  } = t0;
  const {
    uiStore,
    userStore
  } = useContext(RootStoreContext);
  const lang = userStore.user.settings.lang as SupportedLanguageKeys;
  const {
    searchParams,
    setParam,
    setParams
  } = useLFSearchParams();
  let t1;
  if ($[0] !== paramKey || $[1] !== searchParams) {
    t1 = searchParams.get(paramKey);
    $[0] = paramKey;
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selectedParam = t1;
  let enabled;
  let queryKey;
  let t2;
  if ($[3] !== parents || $[4] !== searchParams) {
    const parentDeps = (parents ?? []).map(_temp);
    let t3;
    if ($[8] !== searchParams) {
      t3 = d_0 => ({
        dep: d_0,
        value: searchParams.get(d_0.paramKey)
      });
      $[8] = searchParams;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    const parentParams = parentDeps.map(t3);
    enabled = parentParams.filter(_temp2).every(_temp3);
    queryKey = parentParams.reduce(_temp4, []);
    t2 = parentParams.reduce(_temp5, {});
    $[3] = parents;
    $[4] = searchParams;
    $[5] = enabled;
    $[6] = queryKey;
    $[7] = t2;
  } else {
    enabled = $[5];
    queryKey = $[6];
    t2 = $[7];
  }
  const fetchArgs = t2;
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      country: "countries",
      province: "regions",
      district: "districts",
      subdistrict: "subdistricts",
      village: "villages"
    };
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const collectionKey = (t3 as const)[level] as CollectionKey<L>;
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== collectionKey) {
    t5 = {
      [collectionKey]: t4 as Labeled[]
    };
    $[12] = collectionKey;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const defaultData = t5 as Record<CollectionKey<L>, Labeled[]>;
  let t6;
  if ($[14] !== defaultData || $[15] !== fetchArgs || $[16] !== uiStore) {
    t6 = () => uiStore.currentOrganization ? uiStore.currentOrganization.fetchAdministrativeLevels(fetchArgs as any).then(t7 => {
      const {
        data
      } = t7;
      return (data ?? defaultData) as unknown as LevelDataMap[L];
    }).catch(() => defaultData as LevelDataMap[L]) : Promise.resolve(defaultData as LevelDataMap[L]);
    $[14] = defaultData;
    $[15] = fetchArgs;
    $[16] = uiStore;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== enabled || $[19] !== level || $[20] !== queryKey || $[21] !== t6) {
    t7 = {
      level,
      enabled,
      queryKey,
      fetchAdministrativeLevels: t6
    };
    $[18] = enabled;
    $[19] = level;
    $[20] = queryKey;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const {
    data: t8,
    isLoading
  } = useAdministrativeLevelsQuery(t7);
  const data_0 = t8 === undefined ? defaultData : t8;
  const dataForIndex = data_0 as LevelDataMap[L] & DataForLevel<L>;
  let t9;
  if ($[23] !== collectionKey || $[24] !== dataForIndex) {
    t9 = dataForIndex[collectionKey] ?? [];
    $[23] = collectionKey;
    $[24] = dataForIndex;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const items = t9;
  let t10;
  bb0: {
    if (!selectedParam || !isPresent(items)) {
      t10 = null;
      break bb0;
    }
    let t11;
    if ($[26] !== items || $[27] !== selectedParam) {
      t11 = items.find(c => [c.en, c.native].some(v => v === selectedParam)) ?? null;
      $[26] = items;
      $[27] = selectedParam;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    t10 = t11;
  }
  const active = t10;
  let t11;
  bb1: {
    if (!active) {
      t11 = selectedParam ?? undefined;
      break bb1;
    }
    t11 = active[lang] || active.en || active.native;
  }
  const activeLabel = t11;
  let t12;
  if ($[29] !== level || $[30] !== paramKey || $[31] !== setParam || $[32] !== setParams) {
    t12 = next => {
      if (next === null) {
        const updates = dependentLevels(level).reduce(_temp6, [{
          key: paramKey,
          value: null
        }]);
        setParams(updates);
      } else {
        setParam(paramKey, next);
      }
    };
    $[29] = level;
    $[30] = paramKey;
    $[31] = setParam;
    $[32] = setParams;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const setActive = t12;
  let t13;
  if ($[34] !== active || $[35] !== activeLabel || $[36] !== isLoading || $[37] !== items || $[38] !== selectedParam || $[39] !== setActive) {
    t13 = {
      selectedParam,
      items,
      active,
      activeLabel,
      isLoading,
      setActive
    };
    $[34] = active;
    $[35] = activeLabel;
    $[36] = isLoading;
    $[37] = items;
    $[38] = selectedParam;
    $[39] = setActive;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  return t13;
};
function _temp(d) {
  return {
    ...d,
    required: d.required ?? true
  };
}
function _temp2(p) {
  return p.dep.required;
}
function _temp3(p_0) {
  return !!p_0.value;
}
function _temp4(acc, p_1) {
  if (p_1.value != null) {
    acc.push(p_1.value);
  }
  return acc;
}
function _temp5(acc_0, p_2) {
  if (p_2.value) {
    acc_0[p_2.dep.argName] = p_2.value;
  }
  return acc_0;
}
function _temp6(acc_1, lvl) {
  acc_1.push({
    key: PARAM_KEY_BY_LEVEL[lvl],
    value: null
  });
  return acc_1;
}